import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type OperationsGroupedBy = 'city' | 'operators';

export type OperationsViewMode = 'table' | 'card';

export type OperationsCurrentOperator = {
  operator?: string;
  open: boolean;
};

export type TourOperatorsSheetView = 'assignments' | 'manage';

export type OperationsUIState = {
  selected: Map<string, boolean>;
  expanded: Map<string, boolean>;
  groupedBy?: OperationsGroupedBy;
  groupedByExpandedAll: boolean;
  viewMode: OperationsViewMode;
  currentOperator?: OperationsCurrentOperator;
  tourOperatorsSheetOpen: boolean;
  tourOperatorsSheetView: TourOperatorsSheetView;
  tourOperatorFormOpen: boolean;
  editingTourOperatorId: string | null;
  supplierDialogItemIds: string[] | null;
  subitemFormParentId: string | null;
  transformDialogItemId: string | null;
  // Item whose edit modal should be open — set when returning from the planner
  // item editor (sessionStorage `open-item`) so the consultant lands back on
  // the same modal they left.
  editItemId: string | null;
};

type OperationsUIStore = OperationsUIState & {
  toggleSelected: (id: string) => void;
  setSelected: (id: string, selected: boolean) => void;
  clearSelected: () => void;
  toggleExpanded: (id: string) => void;
  setExpanded: (id: string, expanded: boolean) => void;
  setGroupedBy: (groupedBy: OperationsGroupedBy | undefined) => void;
  setGroupedByExpandedAll: (expanded: boolean) => void;
  setViewMode: (viewMode: OperationsViewMode) => void;
  setCurrentOperator: (current: OperationsCurrentOperator | undefined) => void;
  setTourOperatorsSheetOpen: (open: boolean) => void;
  setTourOperatorsSheetView: (view: TourOperatorsSheetView) => void;
  openTourOperatorForm: (id: string | null) => void;
  closeTourOperatorForm: () => void;
  openSupplierDialog: (itineraryItemIds: string[]) => void;
  closeSupplierDialog: () => void;
  openSubitemForm: (parentId: string) => void;
  closeSubitemForm: () => void;
  openTransformDialog: (itemId: string) => void;
  closeTransformDialog: () => void;
  openEditItem: (itemId: string) => void;
  closeEditItem: () => void;
  reset: () => void;
};

export const initialOperationsUIState: OperationsUIState = {
  selected: new Map(),
  expanded: new Map(),
  groupedByExpandedAll: false,
  // Default to the detailed rows; 'card' is the compact-grid opt-in.
  viewMode: 'table',
  tourOperatorsSheetOpen: false,
  tourOperatorsSheetView: 'assignments',
  tourOperatorFormOpen: false,
  editingTourOperatorId: null,
  supplierDialogItemIds: null,
  subitemFormParentId: null,
  transformDialogItemId: null,
  editItemId: null,
};

export const useOperationsUIStore = create<OperationsUIStore>()(
  persist(
    (set) => ({
      ...initialOperationsUIState,
      toggleSelected: (id) =>
        set(({ selected }) => {
          const next = new Map(selected);
          if (next.get(id)) {
            next.delete(id);
          } else {
            next.set(id, true);
          }
          return { selected: next };
        }),
      setSelected: (id, value) =>
        set(({ selected }) => {
          const next = new Map(selected);
          if (value) {
            next.set(id, true);
          } else {
            next.delete(id);
          }
          return { selected: next };
        }),
      clearSelected: () => set({ selected: new Map() }),
      toggleExpanded: (id) =>
        set(({ expanded }) => {
          const next = new Map(expanded);
          if (next.get(id)) {
            next.delete(id);
          } else {
            next.set(id, true);
          }
          return { expanded: next };
        }),
      setExpanded: (id, value) =>
        set(({ expanded }) => {
          const next = new Map(expanded);
          if (value) {
            next.set(id, true);
          } else {
            next.delete(id);
          }
          return { expanded: next };
        }),
      setGroupedBy: (groupedBy) => set({ groupedBy }),
      setGroupedByExpandedAll: (groupedByExpandedAll) =>
        set({ groupedByExpandedAll }),
      setViewMode: (viewMode) => set({ viewMode }),
      setCurrentOperator: (currentOperator) => set({ currentOperator }),
      setTourOperatorsSheetOpen: (tourOperatorsSheetOpen) =>
        set(
          tourOperatorsSheetOpen
            ? { tourOperatorsSheetOpen }
            : {
                tourOperatorsSheetOpen,
                tourOperatorFormOpen: false,
                editingTourOperatorId: null,
              }
        ),
      setTourOperatorsSheetView: (tourOperatorsSheetView) =>
        set({ tourOperatorsSheetView }),
      openTourOperatorForm: (editingTourOperatorId) =>
        set({ tourOperatorFormOpen: true, editingTourOperatorId }),
      closeTourOperatorForm: () =>
        set({ tourOperatorFormOpen: false, editingTourOperatorId: null }),
      openSupplierDialog: (itineraryItemIds) =>
        set({ supplierDialogItemIds: itineraryItemIds }),
      closeSupplierDialog: () => set({ supplierDialogItemIds: null }),
      openSubitemForm: (parentId) => set({ subitemFormParentId: parentId }),
      closeSubitemForm: () => set({ subitemFormParentId: null }),
      openTransformDialog: (itemId) => set({ transformDialogItemId: itemId }),
      closeTransformDialog: () => set({ transformDialogItemId: null }),
      openEditItem: (editItemId) => set({ editItemId }),
      closeEditItem: () => set({ editItemId: null }),
      reset: () =>
        set({
          ...initialOperationsUIState,
          selected: new Map(),
          expanded: new Map(),
        }),
    }),
    {
      // Only the display preferences persist between visits (RIO-19); the
      // selection/expansion Maps and dialog flags stay session-transient.
      name: 'operations-ui',
      storage: createJSONStorage(() => window.localStorage),
      partialize: (state) => ({
        viewMode: state.viewMode,
        groupedBy: state.groupedBy,
      }),
    }
  )
);

// Public read-only selector — hides the internal `expanded` Map shape so tests
// and components read expansion through a stable API, not `.expanded.get(id)`.
export const isExpanded = (id: string): boolean =>
  useOperationsUIStore.getState().expanded.get(id) ?? false;
